import { Heading, TroopsData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Cell, headingBetween, headingOffset, opposite } from "../world/cell";
import { Grid } from "../world/grid";
import { RoadMap } from "../world/roadMap";
import { Enemy, Troop } from "./entities";
import { FormationSlot } from "./troopRules";

/**
 * The troops' geometry (split from troops.ts, pure moves): which way a formation faces, where a slot of it stands, the
 * raider the formation strikes, and the BFS route to the flag. TroopSystem (and the Barracks through it) calls these.
 */

/** Neighbour order for the BFS — fixed so ties resolve the same way on every platform. */
const HEADINGS: readonly Heading[] = [Heading.North, Heading.East, Heading.South, Heading.West];

/** The way a formation at cell `r` faces (TroopSystem.facing): toward the portal along the road, else its main axis. */
export function formationFacing(road: RoadMap, grid: Grid, r: Cell): Heading {
  const cells = road.roadCells;
  let i = -1;
  for (let k = 0; k < cells.length; k++) if (cells[k].equals(r)) { i = k; break; }
  if (i > 0) return headingBetween(r, cells[i - 1]);
  if (i === 0 && cells.length > 1) return opposite(headingBetween(r, cells[1]));
  const p = road.portal, dx = p.x - r.x, dy = p.y - r.y;
  if (dx === 0 && dy === 0) return grid.baseHeading;
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? Heading.East : Heading.West;
  return dy > 0 ? Heading.North : Heading.South;
}

/** Where formation slot `f` at cell `at` stands, facing `facing`. */
export function formationPointAt(rules: TroopsData, at: Cell, f: FormationSlot, facing: Heading): { readonly x: Fix64; readonly y: Fix64 } {
  const [fx, fy] = headingOffset(facing);
  const [lx, ly] = headingOffset(((facing + 1) & 3) as Heading);
  const depth = rules.formationSpacing.mulInt(f.depth), lateral = rules.formationLateralSpacing.mulInt(f.lateral).divInt(2);
  return {
    x: Fix64.fromInt(at.x).add(depth.mulInt(fx)).add(lateral.mulInt(lx)),
    y: Fix64.fromInt(at.y).add(depth.mulInt(fy)).add(lateral.mulInt(ly)),
  };
}

/** Of the ground raiders within `reach` of troop `t`, the one nearest the flag `rally` (ties to the lower id). */
export function nearestToFlag(reach: Fix64, rally: Cell, t: Troop, enemies: readonly Enemy[]): Enemy | null {
  const r2 = reach.mul(reach);
  const fx = Fix64.fromInt(rally.x), fy = Fix64.fromInt(rally.y);
  let best: Enemy | null = null; let bestD2 = Fix64.Zero;
  for (const e of enemies) {
    if (!e.alive || e.isAir || e.burrowed) continue;
    const dx = e.x.sub(t.x), dy = e.y.sub(t.y);
    if (dx.mul(dx).add(dy.mul(dy)).gt(r2)) continue;
    const gx = e.x.sub(fx), gy = e.y.sub(fy);
    const d2 = gx.mul(gx).add(gy.mul(gy));
    if (best === null || d2.lt(bestD2) || (d2.eq(bestD2) && e.id < best.id)) { best = e; bestD2 = d2; }
  }
  return best;
}

/** BFS from `from` to `to` over `walkable` cells; the cells after `from` (empty when already there or unreachable). */
export function troopRoute(grid: Grid, walkable: (c: Cell) => boolean, from: Cell, to: Cell): Cell[] {
  if (from.equals(to)) return [];
  const w = grid.width, size = w * grid.height;
  const prev = new Int32Array(size).fill(-1);
  const queue: number[] = [grid.index(from)];
  prev[grid.index(from)] = grid.index(from);
  const goal = grid.index(to);
  for (let qi = 0; qi < queue.length; qi++) {
    const i = queue[qi];
    if (i === goal) break;
    const c = new Cell(i % w, Math.floor(i / w));
    for (const h of HEADINGS) {
      const n = c.step(h);
      if (!walkable(n)) continue;
      const ni = grid.index(n);
      if (prev[ni] >= 0) continue;
      prev[ni] = i;
      queue.push(ni);
    }
  }
  if (prev[goal] < 0) return [];
  const out: Cell[] = [];
  for (let i = goal; i !== grid.index(from); i = prev[i]) out.push(new Cell(i % w, Math.floor(i / w)));
  return out.reverse();
}
